import { useEffect, useState } from 'react';
import { DASH_SET_TEXT, formatNumberOf, type DashCard, type DashFilter, type DashSet, type Widget } from '@kaba/shared';
import { api } from '../../lib/api';
import { Button, Sheet } from '../ui/ui';
import { CardRow, Empty } from './widgets';

export type ItemsRequest =
  | { kind: 'set'; title: string; filter: DashFilter; set: DashSet; sort: 'due' | 'priority' | 'updated' }
  /** 0.86: Karten hinter einer Kennzahl (ganz oder eine Gruppe) – mit Wert je Karte */
  | { kind: 'metric'; title: string; filter: DashFilter; widget: Extract<Widget, { type: 'metric' }>; group: string | null }
  /** 0.87: Karten hinter einer Gruppe (Verteilung, Auslastung, Termintreue) */
  | { kind: 'group'; title: string; filter: DashFilter; widget: Widget; key: string; label: string };

type Item = DashCard & { value?: unknown };

/** Vollständige Trefferliste hinter einer Zahl – dieselbe Rechnung wie im Baustein (A04), seitenweise */
export function ItemsSheet({ req, onClose }: { req: ItemsRequest | null; onClose: () => void }) {
  const [items, setItems] = useState<Item[]>([]);
  const [withValue, setWithValue] = useState<number | null>(null);
  const [field, setField] = useState<{ name: string; type: string; config: unknown } | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const load = async (offset: number) => {
    if (!req) return;
    setBusy(true);
    try {
      const r =
        req.kind === 'metric'
          ? await api.post<{ total: number; withValue: number; items: Item[] }>('/api/dashboard-data/metric-items', { filter: req.filter, widget: req.widget, group: req.group, offset, limit: 50 })
          : req.kind === 'group'
            ? await api.post<{ total: number; items: Item[] }>('/api/dashboard-data/group-items', { filter: req.filter, widget: req.widget, key: req.key, offset, limit: 50 })
            : await api.post<{ total: number; items: Item[] }>('/api/dashboard-data/items', { filter: req.filter, set: req.set, sort: req.sort, offset, limit: 50 });
      setTotal(r.total);
      if ('withValue' in r) setWithValue(r.withValue as number);
      setItems((prev) => (offset ? [...prev, ...r.items] : r.items));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    setItems([]);
    setTotal(null);
    setWithValue(null);
    setError(null);
    setField(null);
    if (req?.kind === 'metric')
      // Feld für die Formatierung der Werte (Einheit/Währung) aus den Kennzahldaten
      void api
        .post<{ results: { status: string; data?: { field?: { name: string; type: string; config: unknown } } }[] }>('/api/dashboard-data', { filter: req.filter, widgets: [req.widget] })
        .then((d) => setField(d.results[0]?.data?.field ?? null))
        .catch(() => undefined);
    if (req) void load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [req]);
  return (
    <Sheet open={!!req} onOpenChange={(o) => !o && onClose()} title={req ? (req.kind === 'metric' ? `${req.title}${req.group !== null ? `: ${req.group}` : ''}` : req.kind === 'group' ? `${req.title}: ${req.label}` : `${req.title}: ${DASH_SET_TEXT[req.set]}`) : 'Treffer'} side="center">
      <div className="flex flex-col gap-2 p-4">
        <p className="text-sm text-[var(--ink-soft)]" role="status" aria-live="polite">
          {error ? '' : total === null ? 'Lädt …' : total === 1 ? '1 Karte' : `${total} Karten`}
          {withValue !== null && total !== null && withValue < total ? ` · ${total - withValue} ohne Wert (zählen nicht mit)` : ''}
        </p>
        {error && (
          <p role="alert" className="rounded-lg bg-rose-50 p-3 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-200">
            {error}
          </p>
        )}
        {total === 0 && <Empty>Keine passenden Aufgaben.</Empty>}
        <ul>
          {items.map((c) => (
            <CardRow
              key={c.id}
              c={c}
              extra={
                req?.kind === 'metric'
                  ? c.value === null || c.value === undefined || c.value === ''
                    ? 'ohne Wert'
                    : field && typeof c.value === 'number'
                      ? formatNumberOf({ id: '', name: field.name, type: field.type as 'number', options: [], config: field.config as null }, c.value)
                      : String(c.value)
                  : undefined
              }
            />
          ))}
        </ul>
        {total !== null && items.length < total && (
          <Button className="self-start" disabled={busy} onClick={() => void load(items.length)}>
            Weitere laden ({total - items.length})
          </Button>
        )}
      </div>
    </Sheet>
  );
}
